import type { ApiClient } from '@classgo/app-kit';
import type {
  AnnouncementItem,
  GradesView,
  ReportCard,
  StudentDetail,
  StudentsPage,
  TeacherListItem,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

const qs = (query: Record<string, string | number | undefined>) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
};

/**
 * The read views of the school (`/school/students`, `/school/teachers`, `/school/period-grades`,
 * report cards and announcements), as query options so screens and loaders share the cache. The
 * timetable's live in `timetable/api.ts` and the attendance's in `attendance/api.ts`, with their
 * writes.
 */
export const recordQueries = {
  students: (
    api: ApiClient,
    query: {
      sedeId?: string;
      gradeId?: string;
      groupId?: string;
      search?: string;
      page?: number;
      pageSize?: number;
    },
  ) =>
    queryOptions({
      queryKey: ['school', 'students', query],
      queryFn: () => api.request<StudentsPage>(`/school/students${qs({ pageSize: 50, ...query })}`),
    }),
  student: (api: ApiClient, id: string) =>
    queryOptions({
      queryKey: ['school', 'students', 'detail', id],
      queryFn: () => api.request<StudentDetail>(`/school/students/${id}`),
    }),
  reportCard: (api: ApiClient, id: string, periodId: string) =>
    queryOptions({
      queryKey: ['school', 'report-card', id, periodId],
      queryFn: () =>
        api.request<ReportCard>(`/school/students/${id}/report-card${qs({ periodId })}`),
      enabled: id !== '' && periodId !== '',
    }),
  teachers: (api: ApiClient) =>
    queryOptions({
      queryKey: ['school', 'teachers'],
      queryFn: () => api.request<TeacherListItem[]>('/school/teachers'),
    }),
  grades: (api: ApiClient, groupId: string, subjectId: string, periodId: string) =>
    queryOptions({
      queryKey: ['school', 'period-grades', groupId, subjectId, periodId],
      queryFn: () =>
        api.request<GradesView>(`/school/period-grades${qs({ groupId, subjectId, periodId })}`),
      enabled: groupId !== '' && subjectId !== '' && periodId !== '',
    }),
  announcements: (api: ApiClient) =>
    queryOptions({
      queryKey: ['school', 'announcements'],
      queryFn: () => api.request<AnnouncementItem[]>('/school/announcements'),
    }),
};
